import { ClientRequestItemStatus, ClientRequestModel, ClientRequestStatus, ClientRequestType, RequestItem } from '@aiwyn/api';
import { CxpClientRequestTaskModel, CxpTask, CxpTaskIntegration, CxpTaskProgress, CxpTaskStatus, CxpTaskType } from '../../models';
import { isUrlSafe } from '../../../../common/security/utility/url-safety.utils';

/**
 * Items the user can see / interact with. A request item is visible when
 * either it isn't restricted, or one of the user's contacts is in its
 * assignees list. Mirrors the legacy `isRequestItemRestricted` helper.
 */
function getVisibleItems(model: ClientRequestModel, contactIds: number[]): RequestItem[] {
  const items = model.requestItems ?? [];
  return items.filter((item) => {
    if (!item.restricted) return true;
    return item.assignees?.some((a) => contactIds.includes(a.contactId)) ?? false;
  });
}

/**
 * Maps a `ClientRequestModel` to a `CxpTask`, or returns `null` to drop the
 * row from the tasks list entirely. The mapper drops a row when it carries
 * FieldGuide markers but no safe deep link — emitting such a row would
 * either expose an unsafe URL to the redirect path or route the user into
 * the CXP detail container with no items to show.
 */
/**
 * Which of the three task types a client request surfaces as.
 *
 * The two special cases are mutually exclusive in practice: a FieldGuide-backed request is
 * created by that integration and an eSign shell by a bundle send, so nothing produces a row
 * that is both. The order below is therefore not a precedence rule, only the order the checks
 * happen to read in.
 */
function resolveTaskType(model: CxpClientRequestTaskModel, isExternal: boolean): CxpTaskType {
  if (isExternal) return 'external-client-request-list';
  if (isEsignRequest(model)) return 'esign-client-request-list';
  return 'client-request-list';
}

export function mapClientRequestToTask(model: CxpClientRequestTaskModel, contactIds: number[]): CxpTask | null {
  const visibleItems = getVisibleItems(model, contactIds);
  const status = deriveStatusFromVisibleItems(model.status, visibleItems);
  const progress = mapVisibleItemsProgress(visibleItems);

  const fieldGuideMarker = isFieldGuideRequest(model);
  const fieldGuideLink = fieldGuideMarker ? getFieldGuideDeepLink(model) : null;
  if (fieldGuideMarker && !isUrlSafe(fieldGuideLink)) return null;

  const isExternal = fieldGuideLink !== null;
  const type = resolveTaskType(model, isExternal);
  const integration: CxpTaskIntegration | null = isExternal
    ? { type: 'fieldguide', url: fieldGuideLink }
    : null;

  return {
    key: `${type}-${model.id}`,
    id: model.id,
    type,
    sourceId: model.id,
    name: model.name,
    description: model.description ?? '',
    createdAt: model.createdAt ?? null,
    // Backend mapping for due date pending. When the field lands on
    // ClientRequestModel, populate it here and the row template will pick it
    // up automatically.
    dueDate: null,
    updatedAt: model.updatedAt ?? null,
    // Client-request status comes from the source record itself, not from
    // a "viewed" event — leaving lastViewedAt null.
    lastViewedAt: null,
    status,
    sourceStatus: model.status,
    actionRequired: visibleItems.some((item) => item.status === ClientRequestItemStatus.REJECTED),
    progress,
    staffIds: model.reviewerStaffId ? [model.reviewerStaffId] : [],
    clientId: model.clientId,
    jobId: model.jobId ?? null,
    integration,
    serviceLine: null,
    clientName: null,
    projectName: model.projectName ?? null,
  };
}

/**
 * An `E_SIGN` request is the client-request shell of an eSign bundle: the bundle
 * carries the files and the recipients, so the shell has no items of its own.
 * That makes `status` fall out of the request record alone (`DONE` → completed)
 * and `progress` null — the per-signer "Signature Required / Completed" states
 * the Figma calls for need recipient data the portal cannot read yet.
 */
function isEsignRequest(model: ClientRequestModel): boolean {
  return model.type === ClientRequestType.E_SIGN;
}

function isFieldGuideRequest(model: ClientRequestModel): boolean {
  const sysRefs = model.sysRefs;
  if (!sysRefs) return false;
  return Object.keys(sysRefs).some((key) => key.includes('field_guide') && sysRefs[key]?.ref);
}

function getFieldGuideDeepLink(model: ClientRequestModel): string | null {
  const link = model.sysRefs?.['field_guide_deep_link'];
  return link?.href ?? link?.ref ?? null;
}

function isItemCompleted(item: RequestItem): boolean {
  return item.isNotApplicable || item.status === ClientRequestItemStatus.DONE || item.status === ClientRequestItemStatus.IN_REVIEW;
}

/**
 * Derives the user's task status from the items they can actually act on.
 *
 * Rules:
 *   - Request marked DONE by the firm → always 'completed' (firm closed it)
 *   - User has no visible items → 'open' (defensive; should be rare since
 *     the request wouldn't be returned for a user with no access)
 *   - All visible items are DONE / IN_REVIEW / not-applicable → 'completed'
 *   - Otherwise → 'open'
 */
function deriveStatusFromVisibleItems(requestStatus: ClientRequestStatus, visibleItems: RequestItem[]): CxpTaskStatus {
  if (requestStatus === ClientRequestStatus.DONE) return 'completed';
  if (visibleItems.length === 0) return 'open';
  return visibleItems.every(isItemCompleted) ? 'completed' : 'open';
}

function mapVisibleItemsProgress(visibleItems: RequestItem[]): CxpTaskProgress | null {
  if (!visibleItems.length) return null;
  const completed = visibleItems.filter(isItemCompleted).length;
  return { completed, total: visibleItems.length };
}
